import { useCallback, useRef, useState, type ChangeEvent } from 'react';
import { CheckCircle2, File as FileIcon, Loader2, Paperclip, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { uploadFile } from '@/lib/api/upload';

import type { QuestionRendererProps, UploadedFileMetadata } from '../types';

interface UploadingFile {
  id: string;
  file: File;
  localPreview?: string;
  progress: number;
}

function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) {
    return '0 Bytes';
  }

  const units = ['Bytes', 'KB', 'MB', 'GB'];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const size = bytes / Math.pow(1024, index);

  return `${Math.round(size * 100) / 100} ${units[index]}`;
}

function isImageType(mimeType: string): boolean {
  return mimeType.startsWith('image/');
}

export const FileUploadQuestion = ({
  question,
  value = [],
  onChange,
  onBlur,
  sessionToken,
  ensureSessionToken,
  error,
  mode,
  disabled,
}: QuestionRendererProps<UploadedFileMetadata[]>) => {
  if (question.type !== 'file_upload') {
    return null;
  }

  const [uploadingFiles, setUploadingFiles] = useState<UploadingFile[]>([]);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const maxFiles = question.maxFiles ?? 1;
  const maxSizeMb = question.maxSizeMb ?? 10;
  const isMultiple = maxFiles > 1;
  const isUploading = uploadingFiles.length > 0;
  const uploadedCount = value.length;
  const canAddMore = uploadedCount < maxFiles && !isUploading;
  const isInteractive = mode === 'runtime' && !disabled;
  const combinedError = error || uploadError || undefined;

  const accepts = question.accept?.length ? question.accept.join(', ') : 'Any file type';
  const maxSizeLabel = `${maxSizeMb} MB`;
  const maxFilesLabel = `${maxFiles} file${maxFiles > 1 ? 's' : ''}`;

  const validateFile = useCallback(
    (file: File): string | null => {
      const maxSizeBytes = maxSizeMb * 1024 * 1024;

      if (file.size > maxSizeBytes) {
        return `File size exceeds ${maxSizeMb}MB limit`;
      }

      if (question.accept && question.accept.length > 0) {
        const extension = file.name.includes('.') ? `.${file.name.split('.').pop()?.toLowerCase()}` : '';
        const mimeType = file.type;

        const isAllowed = question.accept.some((accepted) => {
          if (accepted.endsWith('/*')) {
            const prefix = accepted.replace('/*', '');
            return mimeType.startsWith(prefix);
          }

          return accepted.toLowerCase() === mimeType.toLowerCase() || accepted.toLowerCase() === extension;
        });

        if (!isAllowed) {
          return `File type not allowed. Accepted: ${question.accept.join(', ')}`;
        }
      }

      return null;
    },
    [question.accept, maxSizeMb]
  );

  const handleFileSelect = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      if (!isInteractive) {
        return;
      }

      const files = Array.from(event.target.files ?? []);
      if (files.length === 0) {
        return;
      }

      setUploadError(null);

      // Check total file limit
      const totalAfterUpload = uploadedCount + files.length;
      if (totalAfterUpload > maxFiles) {
        setUploadError(`Maximum ${maxFiles} file${maxFiles > 1 ? 's' : ''} allowed. You can add ${maxFiles - uploadedCount} more.`);
        if (inputRef.current) inputRef.current.value = '';
        return;
      }

      // Validate all files before upload
      for (const file of files) {
        const validationError = validateFile(file);
        if (validationError) {
          setUploadError(validationError);
          if (inputRef.current) inputRef.current.value = '';
          return;
        }
      }

      // Create uploading entries with local previews
      const newUploadingFiles: UploadingFile[] = files.map((file) => ({
        id: crypto.randomUUID(),
        file,
        localPreview: isImageType(file.type) ? URL.createObjectURL(file) : undefined,
        progress: 0,
      }));

      setUploadingFiles((prev) => [...prev, ...newUploadingFiles]);

      // Upload each file
      const newUploadedFiles: UploadedFileMetadata[] = [];

      for (const uploadingFile of newUploadingFiles) {
        try {
          const resolvedSessionToken = sessionToken ?? (await ensureSessionToken?.());
          if (!resolvedSessionToken) {
            throw new Error('Session token required');
          }
          const metadata = await uploadFile(uploadingFile.file, {
            onProgress: (progress) => {
              setUploadingFiles((prev) =>
                prev.map((f) => (f.id === uploadingFile.id ? { ...f, progress } : f))
              );
            },
            sessionToken: resolvedSessionToken,
          });

          // Remove from uploading
          setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadingFile.id));

          // Clean up local preview
          if (uploadingFile.localPreview) {
            URL.revokeObjectURL(uploadingFile.localPreview);
          }

          newUploadedFiles.push(metadata);
        } catch (err) {
          setUploadingFiles((prev) => prev.filter((f) => f.id !== uploadingFile.id));
          if (uploadingFile.localPreview) {
            URL.revokeObjectURL(uploadingFile.localPreview);
          }
          setUploadError(err instanceof Error ? err.message : 'Upload failed');
        }
      }

      // Update value with all successfully uploaded files
      if (newUploadedFiles.length > 0) {
        onChange?.([...value, ...newUploadedFiles]);
        onBlur?.();
      }

      // Reset input
      if (inputRef.current) {
        inputRef.current.value = '';
      }
    },
    [ensureSessionToken, isInteractive, maxFiles, uploadedCount, validateFile, sessionToken, value, onChange, onBlur]
  );

  const handleRemove = useCallback(
    (index: number) => {
      if (!isInteractive) {
        return;
      }

      const newValue = [...value];
      newValue.splice(index, 1);
      onChange?.(newValue);
      setUploadError(null);
      onBlur?.();
    },
    [isInteractive, value, onChange, onBlur]
  );

  const handleTriggerInput = useCallback(() => {
    if (isInteractive && canAddMore) {
      inputRef.current?.click();
    }
  }, [isInteractive, canAddMore]);

  return (
    <div className="space-y-3">
      {mode === 'preview' ? (
        <div className="flex flex-col items-center justify-center gap-3 rounded-lg border border-dashed border-border/60 bg-muted/20 px-5 py-8 text-center">
          <Paperclip className="size-8 text-muted-foreground" aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-foreground">
              File uploads will be enabled in the live form
            </p>
            <p className="text-xs text-muted-foreground">Preview only</p>
          </div>
          <Button variant="secondary" size="sm" disabled className="pointer-events-none">
            Browse files
          </Button>
        </div>
      ) : (
        <>
          {/* Hidden file input */}
          <input
            ref={inputRef}
            id={question.ref}
            type="file"
            className="sr-only"
            accept={question.accept?.join(',')}
            multiple={isMultiple}
            onChange={handleFileSelect}
            disabled={!isInteractive || !canAddMore}
            aria-invalid={Boolean(combinedError)}
            aria-describedby={combinedError ? `error-${question.ref}` : undefined}
          />

          {/* Drop zone - show when no files and not uploading */}
          {uploadedCount === 0 && !isUploading && (
            <button
              type="button"
              onClick={handleTriggerInput}
              disabled={!isInteractive}
              className="flex w-full cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed border-border/60 bg-muted/20 px-5 py-8 text-center transition-colors hover:border-primary/50 hover:bg-muted/30 disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Paperclip className="size-8 text-muted-foreground" aria-hidden="true" />
              <div className="space-y-1">
                <p className="text-sm font-medium text-foreground">
                  {isMultiple ? 'Choose files or drag and drop' : 'Choose a file or drag and drop'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {accepts} • Max {maxSizeMb}MB
                </p>
              </div>
            </button>
          )}

          {/* Uploading files list */}
          {uploadingFiles.length > 0 && (
            <div className="space-y-2">
              {uploadingFiles.map((file) => (
                <div key={file.id} className="space-y-2 rounded-lg border border-border bg-muted/20 px-4 py-3">
                  <div className="flex items-center gap-3">
                    {file.localPreview ? (
                      <img
                        src={file.localPreview}
                        alt={`Uploading ${file.file.name}`}
                        className="h-10 w-10 rounded border border-border object-cover"
                      />
                    ) : (
                      <div className="flex h-10 w-10 items-center justify-center rounded border border-border bg-background">
                        <FileIcon className="size-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium">{file.file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {formatFileSize(file.file.size)} • {file.progress}%
                      </p>
                    </div>
                    <Loader2 className="size-5 animate-spin text-primary" />
                  </div>
                  <div className="h-2 w-full rounded-full bg-muted">
                    <div
                      className="h-2 rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${file.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Uploaded files list */}
          {uploadedCount > 0 && (
            <div className="space-y-2">
              {value.map((file, index) => (
                <div
                  key={file.fileKey}
                  className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 px-4 py-3"
                >
                  {isImageType(file.mimeType) ? (
                    <img
                      src={file.url}
                      alt={`Preview of ${file.fileName}`}
                      className="h-14 w-14 rounded-md border border-border object-cover"
                    />
                  ) : (
                    <div className="flex h-14 w-14 items-center justify-center rounded-md border border-border bg-background">
                      <FileIcon className="size-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium">{file.fileName}</p>
                    <p className="text-xs text-muted-foreground">{formatFileSize(file.fileSize)}</p>
                    <div className="mt-1 flex items-center gap-1 text-xs text-primary">
                      <CheckCircle2 className="size-4" aria-hidden="true" />
                      <span>Uploaded successfully</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemove(index)}
                    disabled={!isInteractive}
                    className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground disabled:opacity-50"
                    aria-label={`Remove ${file.fileName}`}
                  >
                    <X className="size-4" />
                  </button>
                </div>
              ))}

              {/* Add more button */}
              {canAddMore && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleTriggerInput}
                  disabled={!isInteractive}
                  className="mt-2"
                >
                  <Paperclip className="mr-2 size-4" />
                  Add {isMultiple ? 'more files' : 'file'}
                </Button>
              )}
            </div>
          )}
        </>
      )}

      {mode === 'runtime' && combinedError && (
        <p className="text-sm text-destructive" id={`error-${question.ref}`}>
          {combinedError}
        </p>
      )}

      <dl className="grid gap-1 text-xs text-muted-foreground sm:grid-cols-3">
        <div>
          <dt className="font-semibold text-foreground/80">Accepts</dt>
          <dd>{accepts}</dd>
        </div>
        <div>
          <dt className="font-semibold text-foreground/80">Max size</dt>
          <dd>{maxSizeLabel}</dd>
        </div>
        <div>
          <dt className="font-semibold text-foreground/80">Files</dt>
          <dd>Up to {maxFilesLabel}</dd>
        </div>
      </dl>
    </div>
  );
};
